// Envelope encryption for a vault item's values, as the browser does it:
// seal with the tenant PUBLIC key, open with the session's handle.
//
// Split from `vault-unlock.ts` because the asymmetry here is the point
// and deserves its own file to state it. Writing a value needs no unlock
// at all — the public half is enough, and the admin panel already has it
// from `getTenantKey`. Reading one needs the private half, which is the
// only reason the unlock flow exists. Keeping both in one small module
// makes that pair legible; keeping them next to the session machinery
// made it look like both were gated.
import {
  b64u,
  decryptValue,
  encryptValue,
  fromUtf8,
  generateDek,
  open,
  seal,
  unb64u,
  utf8,
} from '@stoaria/vault-core'
import { withPrivateKey } from './vault-key-session.ts'

export interface SealedValue {
  ciphertext: string
  nonce: string
  wrappedDek: string
}

/** Envelope-encrypts `plain` under a fresh DEK and seals that DEK to the
 * tenant public key — the client-side half of the rule for every
 * non-`variable` item. Does not need the tenant key to be unlocked. */
export async function sealValueForTenant(
  plain: string,
  tenantPubkey: string,
): Promise<SealedValue> {
  const dek = generateDek()
  const { ciphertext, nonce } = await encryptValue(utf8(plain), dek)
  const wrappedDek = await seal(dek, unb64u(tenantPubkey))
  return {
    ciphertext: b64u(ciphertext),
    nonce: b64u(nonce),
    wrappedDek: b64u(wrappedDek),
  }
}

/** Reads a `password` value back to plaintext for the Reveal button.
 * Needs the tenant key unlocked, and runs entirely on the non-extractable
 * handle — this is the day-to-day path ADR 0009 keeps unreadable. */
export function openValue(row: SealedValue): Promise<string> {
  return withPrivateKey(async (recipient) => {
    const dek = await open(unb64u(row.wrappedDek), recipient)
    const plain = await decryptValue(
      unb64u(row.ciphertext),
      unb64u(row.nonce),
      dek,
    )
    return fromUtf8(plain)
  })
}
